<template>
  <v-container
    fluid
    class="deposit-page main-container responsive-height"
    :class="{ 'deposit-page--dark': $vuetify.theme.dark }"
  >
    <header class="deposit-heading">
      <div>
        <span class="deposit-heading__icon"><v-icon>mdi-wallet-plus-outline</v-icon></span>
        <div>
          <h1>Deposit Funds</h1>
          <p>Choose your preferred payment method to fund your wallet securely.</p>
        </div>
      </div>
      <span class="secure-badge"><v-icon size="17">mdi-shield-check-outline</v-icon>Secure payments</span>
    </header>

    <v-row>
      <v-col cols="12" md="6" sm="6" lg="4" v-for="method in methods" :key="method.id">
        <v-card class="elevation-0 transition-method-card deposit-method-card">
          <v-card-actions class="paymentImage">
            <v-img height="155" contain :src="method?.method_logo_url" :alt="method?.method_logo_url">
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular indeterminate color="amber darken-1"></v-progress-circular>
                </v-row>
              </template>
            </v-img>
          </v-card-actions>
          <v-card-actions>
            <div class="value">
              <span>Method</span>
              <span>{{ method.method_name }}</span>
            </div>
            <v-spacer></v-spacer>
            <div class="value">
              <span>Currency</span>
              <span>{{ getCurrencyName(method.currency_id) }}</span>
            </div>
          </v-card-actions>
          <v-card-actions>
            <div class="value">
              <span>Processing Time</span>
              <span>{{ method.processing_time }}</span>
            </div>
            <v-spacer></v-spacer>
            <div class="value mr-6">
              <span>Fees</span>
              <span>${{ method.commission }}</span>
            </div>
          </v-card-actions>

          <v-card-actions class="pb-0 pt-3">
            <v-dialog transition="scale-transition" origin="center center" max-width="554"
              content-class="deposit-withdraw-popup">
              <template v-slot:activator="{ on, attrs }">
                <v-btn outlined elevation="0" class="text-capitalize btn-account details-button" v-bind="attrs"
                  v-on="on">Details</v-btn>
              </template>
              <template v-slot:default="dialog">
                <v-card class="deposit-detail-card" :class="{ 'deposit-detail-card--dark': $vuetify.theme.dark }">
                  <v-card-title class="rounded-0">{{ method.method_name }} Detail</v-card-title>
                  <v-card-text>
                    <p>Currency: <span>{{ getCurrencyName(method.currency_id) }}</span></p>
                    <p>Fees / Commission: <span>${{ method.commission }}</span></p>
                    <p>Processing Time: <span>{{ method.processing_time }}</span></p>
                    <!-- <p v-if="method.beneficiary_name">Beneficiary Name: <span>{{ method.beneficiary_name }}</span></p>
                    <p v-if="method.beneficiary_address">Beneficiary Address: <span>{{ method.beneficiary_address
                        }}</span></p>
                    <p v-if="method.beneficiary_bank_name">Bank Name: <span>{{ method.beneficiary_bank_name }}</span>
                    </p>
                    <p v-if="method.beneficiary_bank_swift">SWIFT: <span>{{ method.beneficiary_bank_swift }}</span></p> -->
                  </v-card-text>
                  <ol>
                    <li>Payments from third parties are not accepted. The name of the sender must match the name on your
                      trading account.</li>
                    <li>The minimum transaction amount is ${{ method.min_transaction }}</li>
                    <li>The maximum transaction amount is ${{ formatMaxAmount(method.max_transaction) }}</li>
                    <li>Processing time is typically {{ method.processing_time }}. There may be delays if additional
                      verification is required.</li>
                  </ol>
                </v-card>
              </template>
            </v-dialog>

            <v-btn :to="getUrl(method)" elevation="0" class="text-capitalize btn-account select-button">Select</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <section v-if="!methods.length" class="deposit-empty">
      <v-icon size="48">mdi-credit-card-off-outline</v-icon>
      <h2>No deposit methods available</h2>
      <p>Please check again later or contact support for assistance.</p>
    </section>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { DEPOSITMETHODS__ACTIONS } from "@/store/action-types";

export default {
  data: () => ({
    methodImages: {
      'bank-transfer': require("@/assets/payments/bank.png"),
      'cash-transfer': require("@/assets/payments/cash.png"),
      'crypto-transfer': require("@/assets/payments/crypto.png"),
      'google-pay': require("@/assets/payments/gPay.png"),
      'debit-credit-cards': require("@/assets/payments/payAgency.png"),
      'ovy-pay': require("@/assets/payments/ovyPay.png"),
    },
    currencyMap: {
      1: 'USD',
      2: 'EUR',
      3: 'USDT'
    }
  }),
  computed: {
    ...mapState("deposit", ["methods"])
  },
  methods: {
    ...mapActions("deposit", [
      DEPOSITMETHODS__ACTIONS.DEPOSITMETHODS
    ]),
    getMethodImage(pretty_name) {
      return this.methodImages[pretty_name] || '';
    },
    getUrl(method) {
      let basePath;
      switch (method.pretty_name) {
        case 'bank-transfer':
          basePath = '/bank-deposit';
          break;
        case 'cash-transfer':
          basePath = '/cash-deposit';
          break;
        case 'google-pay':
          basePath = '/google-pay';
          break;
        case 'debit-credit-cards':
          basePath = '/debit-credit-cards';
          break;
        case 'crypto-transfer':
          basePath = '/crypto-deposit';
          break;
        case 'cash-transfer':
          basePath = '/cash-deposit';
          break;
        case 'ovy-pay':
          basePath = '/ovy-pay';
          break;
        case 'cash-transfer-via-bank':
          basePath = '/cash-transfer-via-bank';
          break;
        default:
          basePath = '/';
      }
      return `${basePath}?payment_id=${method.id}`;
    },
    getCurrencyName(currencyId) {
      return this.currencyMap[currencyId] || 'USD';
    },
    formatMaxAmount(amount) {
      return Number(amount).toLocaleString();
    }
  },
  async mounted() {
    await this[DEPOSITMETHODS__ACTIONS.DEPOSITMETHODS]();
  }
};
</script>

<style lang="scss" scoped>
.deposit-page {
  --deposit-page: #f7f8fa;
  --deposit-panel: #fff;
  --deposit-soft: #f4f5f7;
  --deposit-border: #dfe2e6;
  --deposit-text: #171a1f;
  --deposit-muted: #626973;
  --deposit-gold: #d99a00;
  box-sizing: border-box;
  max-width: none !important;
  min-height: 100%;
  padding: 16px 18px 24px !important;
  color: var(--deposit-text);
  background: var(--deposit-page);
}
.deposit-page--dark {
  --deposit-page: #030b13;
  --deposit-panel: #07111b;
  --deposit-soft: #0b1621;
  --deposit-border: #293026;
  --deposit-text: #f4f5f7;
  --deposit-muted: #aeb6c0;
  --deposit-gold: #ffc400;
  background: radial-gradient(circle at 68% 14%, rgba(255, 196, 0, .055), transparent 34%), var(--deposit-page);
}
.deposit-heading { min-height: 72px; display: flex; align-items: center; justify-content: space-between; gap: 18px; margin-bottom: 8px; padding: 0 4px; }
.deposit-heading > div { display: flex; align-items: center; gap: 13px; }
.deposit-heading__icon { width: 42px; height: 42px; display: inline-flex; align-items: center; justify-content: center; color: var(--deposit-gold); border: 1px solid rgba(217, 154, 0, .42); border-radius: 9px; background: rgba(217, 154, 0, .09); }
.deposit-heading__icon .v-icon { color: inherit; }
.deposit-heading h1 { margin: 0 0 3px; color: var(--deposit-text); font-size: 23px; line-height: 1.25; }
.deposit-heading p { margin: 0; color: var(--deposit-muted); font-size: 13px; }
.secure-badge { display: inline-flex; align-items: center; gap: 7px; padding: 7px 11px; color: var(--deposit-gold); border: 1px solid rgba(217, 154, 0, .34); border-radius: 7px; background: rgba(217, 154, 0, .07); font-size: 12px; font-weight: 600; }
.secure-badge .v-icon { color: inherit; }
.deposit-method-card { height: 100%; overflow: hidden; color: var(--deposit-text) !important; border: 1px solid var(--deposit-border); border-radius: 10px !important; background: var(--deposit-panel) !important; transition: border-color .18s ease, transform .18s ease, box-shadow .18s ease; }
.deposit-method-card:hover { border-color: var(--deposit-gold); transform: translateY(-2px); box-shadow: 0 10px 26px rgba(43, 34, 5, .08) !important; }
.paymentImage { min-height: 174px; margin: 12px 12px 6px; padding: 12px !important; border: 1px solid var(--deposit-border); border-radius: 8px; background: var(--deposit-soft); }
.deposit-method-card > .v-card__actions:not(.paymentImage) { min-height: 57px; padding: 10px 16px; border-top: 1px solid var(--deposit-border); }
.value { display: flex; min-width: 0; flex-direction: column; gap: 3px; }
.value span:first-child { color: var(--deposit-muted); font-size: 11px; }
.value span:last-child { color: var(--deposit-text); font-size: 13px; font-weight: 600; }
.btn-account { min-width: 104px !important; height: 39px !important; border-radius: 6px !important; font-size: 12px; font-weight: 700; }
.details-button { color: var(--deposit-gold) !important; border-color: var(--deposit-gold) !important; }
.select-button { color: #17130a !important; background: linear-gradient(135deg, #ffd43b, #ffc400) !important; }
.deposit-method-card > .v-card__actions:last-child { gap: 10px; justify-content: flex-end; padding-bottom: 14px !important; }
.deposit-empty { min-height: 360px; display: grid; place-items: center; align-content: center; gap: 8px; color: var(--deposit-muted); border: 1px solid var(--deposit-border); border-radius: 10px; background: var(--deposit-panel); text-align: center; }
.deposit-empty .v-icon { color: var(--deposit-gold); }
.deposit-empty h2, .deposit-empty p { margin: 0; }
.deposit-empty h2 { color: var(--deposit-text); font-size: 18px; }
.deposit-empty p { font-size: 13px; }

@media (max-width: 600px) {
  .deposit-page { padding: 12px 10px 20px !important; }
  .deposit-heading { align-items: flex-start; }
  .deposit-heading p, .secure-badge { display: none; }
  .deposit-heading h1 { font-size: 21px; }
}
</style>

<style lang="scss">
.deposit-withdraw-popup .deposit-detail-card { color: #171a1f !important; border: 1px solid #dfe2e6; border-radius: 10px !important; background: #fff !important; }
.deposit-withdraw-popup .deposit-detail-card--dark { color: #f4f5f7 !important; border-color: #5a470c; background: #07111b !important; }
.deposit-withdraw-popup .deposit-detail-card .v-card__title { color: inherit; border-bottom: 1px solid currentColor; border-color: rgba(217, 154, 0, .28); font-size: 18px; font-weight: 700; }
.deposit-withdraw-popup .deposit-detail-card .v-card__text { padding-top: 18px; color: inherit !important; }
.deposit-withdraw-popup .deposit-detail-card p { display: flex; justify-content: space-between; gap: 20px; color: inherit; }
.deposit-withdraw-popup .deposit-detail-card p span { color: #b47f00; font-weight: 700; }
.deposit-withdraw-popup .deposit-detail-card--dark p span { color: #ffc400; }
.deposit-withdraw-popup .deposit-detail-card ol { margin: 0; padding: 0 34px 22px 46px; color: inherit; }
.deposit-withdraw-popup .deposit-detail-card li { margin-bottom: 8px; line-height: 1.5; }
</style>
